import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import EditAgenciaModal from '@/components/EditAgenciaModal';

export default function SuperAdmin() {
  const [stats, setStats] = useState(null);
  const [agencias, setAgencias] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para o Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAgencia, setSelectedAgencia] = useState(null);

  // 1. Pega o usuário atual para evitar auto-bloqueio
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, listRes] = await Promise.all([
        axiosInstance.get('/admin-saas/dashboard'),
        axiosInstance.get('/admin-saas/agencias')
      ]);
      setStats(statsRes.data);
      setAgencias(listRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (agencia) => {
      setSelectedAgencia(agencia);
      setIsModalOpen(true);
  };

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin w-8 h-8 text-purple-600"/></div>;

  return (
    <div className="p-8 bg-slate-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            ⚡ God Mode (Super Admin)
        </h1>
        <p className="text-slate-500">Gerenciamento total do sistema.</p>
      </div>

      {/* KPIS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-white border-l-4 border-l-purple-500 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-gray-500">MRR Estimado</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">R$ {stats?.mrr_estimado.toLocaleString('pt-BR')}</div></CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-blue-500 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-gray-500">Agências</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{stats?.total_agencias}</div></CardContent>
        </Card>
        <Card className="bg-white border-l-4 border-l-green-500 shadow-sm">
             <CardHeader className="pb-2"><CardTitle className="text-sm uppercase text-gray-500">Usuários</CardTitle></CardHeader>
             <CardContent><div className="text-3xl font-bold">{stats?.total_users}</div></CardContent>
        </Card>
      </div>

      {/* TABELA */}
      <Card className="shadow-lg border-0">
        <CardHeader>
            <CardTitle>Agências Cadastradas</CardTitle>
        </CardHeader>
        <CardContent>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Agência</TableHead>
                        <TableHead>Plano</TableHead>
                        <TableHead>Usuários / Limite</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {agencias.map((ag) => {
                        // 2. Verifica se esta linha é a agência do próprio Super Admin
                        const isMasterAgency = ag.id === currentUser.agencia_id;

                        return (
                            <TableRow key={ag.id} className={isMasterAgency ? "bg-blue-50/40" : ""}>
                                <TableCell>
                                    <div>
                                        <div className="font-bold text-slate-900 flex items-center gap-2">
                                            {ag.nome}
                                            {/* 3. Badge Visual para identificar a própria agência */}
                                            {isMasterAgency && (
                                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100 border border-blue-200 text-[10px]">
                                                    VOCÊ
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-500">{ag.email_contato}</div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline" className="bg-slate-100">{ag.plano}</Badge>
                                </TableCell>
                                <TableCell>
                                    {ag.usuarios_count} / <span className="text-gray-500">{ag.limite_usuarios || 1}</span>
                                </TableCell>
                                <TableCell>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        ag.status_assinatura === 'ATIVA' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                        {ag.status_assinatura}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right">
                                    {/* 4. Esconde o botão se for a Agência Mestre para evitar lockout */}
                                    {!isMasterAgency && (
                                        <Button 
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEditClick(ag)}
                                        >
                                            <Edit className="w-4 h-4 mr-2"/> Gerenciar
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </CardContent>
      </Card>
      
      {/* RENDERIZA O MODAL */}
      <EditAgenciaModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        agencia={selectedAgencia}
        onSuccess={loadData}
      />
      
    </div>
  );
}